import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@mercator-gcp/grout';
import { useAtomValue } from 'jotai';
import { EmptyField } from 'components/elements/EmptyField';
import { Label } from 'components/elements/Label';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import CompanyContact from 'components/widgets/DocumentModals/CompanyContact';
import Contacts from 'components/widgets/DocumentModals/Contacts';
import { InfoModal } from 'components/widgets/InfoModal';
import { UseModalResult } from 'hooks/useModal';
import AIInteractionAPI, {
  type DocumentRelatedContactSuggestionsResponse,
} from 'services/API/AIInteractionAPI';
import { statusSectionInViewAtom } from 'state/contactEnrichment';
import { CompanyItem } from 'types';
import { trackEnrichContactsRequest } from 'utils/analytics';
import { cleanName } from 'utils/companiesInvolved';
import { REGISTERED_ACCESSIBILITY_SPECIALIST_APPLICANT_TITLE } from 'utils/constants';
import { formatCompanyName } from 'utils/format';
import type { DocumentContact } from '@mercator-gcp/types/backend/DocumentContact';

type RelatedContactsProps = {
  contacts?: DocumentContact[] | null;
  companies?: CompanyItem[];
  applicant?: string | null;
  documentId?: string;
  /** Structured suggestions from document detail API (GET v4/documents/:id/). */
  prefetchedAiContactSuggestionsByRelatedContactId?: Record<
    string,
    DocumentRelatedContactSuggestionsResponse
  >;
  Skeleton: UseModalResult<object>['Skeleton'];
};

type ContactWithCompanyDetails = DocumentContact & {
  companyDetails?: CompanyItem;
};

const contactListStyle = {
  gap: 'padding_24',
  width: '100%',
  minWidth: 0, // allow flex/grid children to shrink and use full cell width
};

const DocumentRelatedCompaniesContent = ({
  contacts,
  companies = [],
  applicant,
  documentId,
  prefetchedAiContactSuggestionsByRelatedContactId,
  Skeleton,
}: RelatedContactsProps) => {
  const applicantNames = applicant?.split(';') || [];

  const statusSectionInView = useAtomValue(statusSectionInViewAtom);
  // Contacts we've already kicked off background enrichment for, so the effect
  // never re-triggers a fetch for the same contact on re-render.
  const autoTriggeredRef = useRef<Set<string>>(new Set());

  const [loadingByContactId, setLoadingByContactId] = useState<Record<string, boolean>>(
    {},
  );
  const [resultsByContactId, setResultsByContactId] = useState<
    Record<string, DocumentRelatedContactSuggestionsResponse>
  >({});
  const [errorByContactId, setErrorByContactId] = useState<Record<string, string>>({});

  useEffect(() => {
    if (
      !prefetchedAiContactSuggestionsByRelatedContactId ||
      Object.keys(prefetchedAiContactSuggestionsByRelatedContactId).length === 0
    ) {
      return;
    }
    setResultsByContactId((prev) => ({
      ...prefetchedAiContactSuggestionsByRelatedContactId,
      ...prev,
    }));
  }, [prefetchedAiContactSuggestionsByRelatedContactId]);

  const getCompanyIndexByNameOrAlias = (name: string, company_list: CompanyItem[]) => {
    const cleanedName = cleanName(name);

    return company_list.findIndex(
      (company) =>
        cleanName(company.name) === cleanedName ||
        company.aliases?.map((alias) => cleanName(alias)).includes(cleanedName),
    );
  };

  const { contactList: allContacts, companyList: allCompanies } = useMemo(() => {
    const contactMap = {} as Record<string, ContactWithCompanyDetails[]>;

    // add company details and fill out existing contacts if they match
    const sortedCompanyList = [] as CompanyItem[];
    companies.forEach((company) => {
      if (company.uuid) {
        sortedCompanyList.unshift(company);
      } else {
        sortedCompanyList.push(company);
      }
    });

    // add the related contacts to the list
    // fill out details if there's a matching company already
    contacts?.forEach((contact) => {
      const formattedCompanyName = cleanName(contact?.company || contact?.name);
      const companyIndex = getCompanyIndexByNameOrAlias(
        formattedCompanyName,
        sortedCompanyList,
      );

      // delete from the company list if we have a matching contact
      if (companyIndex > -1) {
        const companyDetails = sortedCompanyList[companyIndex];
        sortedCompanyList.splice(companyIndex, 1);

        if (contact.type === REGISTERED_ACCESSIBILITY_SPECIALIST_APPLICANT_TITLE) {
          // Don't include RA in companies
          return;
        }
        const contactWithDetails: ContactWithCompanyDetails = {
          ...contact,
          companyDetails,
        };
        const cleanCompanyName = cleanName(companyDetails.name);
        if (!contactMap[cleanCompanyName]) {
          contactMap[cleanCompanyName] = [contactWithDetails];
        } else {
          contactMap[cleanCompanyName].push(contactWithDetails);
        }
      } else if (!contactMap[formattedCompanyName]) {
        contactMap[formattedCompanyName] = [contact];
      } else {
        contactMap[formattedCompanyName].push(contact);
      }
    });

    return {
      contactList: Object.values(contactMap).flat(), // flatten because it's a dict of lists
      companyList: sortedCompanyList,
    };
  }, [contacts, companies]);

  const hasNoContent = useMemo(() => {
    return !allCompanies.length && !allContacts.length && !applicantNames.length;
  }, [allCompanies.length, allContacts.length, applicantNames.length]);

  const handleSearchForContact = useCallback(
    async (contact: ContactWithCompanyDetails) => {
      if (!documentId || !contact.id || loadingByContactId[String(contact.id ?? '')]) {
        return;
      }

      trackEnrichContactsRequest({
        contactId: String(contact.id),
        documentId: String(documentId),
      });

      const contactId = String(contact.id);

      // Hide the button as soon as the user clicks (even while polling).
      setLoadingByContactId((prev) => ({ ...prev, [contactId]: true }));
      setErrorByContactId((prev) => ({ ...prev, [contactId]: '' }));

      try {
        const response =
          await AIInteractionAPI.postGenerateDocumentRelatedContactSuggestions({
            documentId,
            documentRelatedContactId: contactId,
          });

        setResultsByContactId((prev) => ({
          ...prev,
          [contactId]: response.data,
        }));
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to generate contact info';
        setErrorByContactId((prev) => ({ ...prev, [contactId]: message }));
      } finally {
        setLoadingByContactId((prev) => ({ ...prev, [contactId]: false }));
      }
    },
    [documentId],
  );

  // Background enrichment: once the user scrolls the document "Status" section
  // into view, auto-enrich contacts missing email/phone. Skips contacts that
  // already have a (prefetched/cached) result, so no redundant AI calls fire.
  // Non-blocking — this runs after render and never gates the modal.
  useEffect(() => {
    if (!statusSectionInView || !documentId) {
      return;
    }

    allContacts.forEach((contact) => {
      const contactId = String(contact.id ?? '');
      if (
        !contact.id ||
        (contact.email && contact.phone) ||
        resultsByContactId[contactId] ||
        loadingByContactId[contactId] ||
        autoTriggeredRef.current.has(contactId)
      ) {
        return;
      }

      autoTriggeredRef.current.add(contactId);
      handleSearchForContact(contact);
    });
  }, [
    statusSectionInView,
    documentId,
    allContacts,
    resultsByContactId,
    loadingByContactId,
    handleSearchForContact,
  ]);

  return (
    <Skeleton variant="text" noOfLines={4}>
      <FlexColumn sx={contactListStyle}>
        {hasNoContent && <EmptyField />}
        {allCompanies.length || allContacts.length ? (
          <>
            {allContacts.map((contact) => (
              <Contacts
                key={String(
                  contact.id ??
                    contact.phone ??
                    contact.email ??
                    contact.name ??
                    contact.type ??
                    'contact',
                )}
                contact={contact}
                documentId={documentId}
                isLoading={!!loadingByContactId[String(contact.id ?? '')]}
                error={errorByContactId[String(contact.id ?? '')]}
                aiSearchResult={resultsByContactId[String(contact.id ?? '')]}
                autoReveal={statusSectionInView}
                onSearch={() => handleSearchForContact(contact)}
              />
            ))}
            {allCompanies.map((company) => (
              <CompanyContact company={company} />
            ))}
          </>
        ) : (
          applicantNames.map((name: string) => (
            <Text>{formatCompanyName(name.trim())}</Text>
          ))
        )}
      </FlexColumn>
    </Skeleton>
  );
};

const DocumentRelatedCompanies = (props: RelatedContactsProps) => {
  return (
    <InfoModal.Row>
      <Label>Companies</Label>
      <InfoModal.RowDetails>
        <DocumentRelatedCompaniesContent {...props} />
      </InfoModal.RowDetails>
    </InfoModal.Row>
  );
};

export default DocumentRelatedCompanies;
export { DocumentRelatedCompaniesContent };
